import { supabase } from './supabase';

/**
 * Saves a generated asset blob directly to the user's Google Drive.
 * Uses the Supabase session's provider_token (which contains the active Google OAuth access token).
 */
export async function saveToGoogleDrive(blob: Blob, filename: string): Promise<{ success: boolean; fileId?: string; error?: string }> {
  try {
    if (!supabase) {
      return { success: false, error: 'Database provider not initialized.' };
    }

    const { data, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !data.session) {
      return { success: false, error: 'Please sign in with Google to enable Google Drive exports.' };
    }

    const providerToken = data.session.provider_token;
    if (!providerToken) {
      return { success: false, error: 'Google permission not found. Please sign out and sign in again to authorize Google Drive access.' };
    }

    // Prepare multipart upload to Google Drive
    const metadata = {
      name: filename || `protolive-asset-${Date.now()}`,
      mimeType: blob.type || 'application/octet-stream',
    };

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', blob);

    const uploadResponse = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${providerToken}`,
      },
      body: form,
    });

    if (!uploadResponse.ok) {
      const errText = await uploadResponse.text();
      console.error('Google Drive Upload failure:', errText);
      return { success: false, error: `Google Drive upload unsuccessful: ${uploadResponse.status} ${uploadResponse.statusText}` };
    }

    const result = await uploadResponse.json();
    return { success: true, fileId: result.id };
  } catch (err: any) {
    console.error('saveToGoogleDrive error:', err);
    return { success: false, error: err?.message || String(err) };
  }
}

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  webViewLink?: string;
  createdTime?: string;
  size?: string;
}

export async function listGoogleDriveFiles(): Promise<{ success: boolean; files?: DriveFile[]; error?: string }> {
  try {
    if (!supabase) {
      return { success: false, error: 'Database provider not initialized.' };
    }

    const { data, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !data.session) {
      return { success: false, error: 'Please sign in with Google to enable Google Drive exports.' };
    }

    const providerToken = data.session.provider_token;
    if (!providerToken) {
      return { success: false, error: 'Google permission not found. Please sign out and sign in again to authorize Google Drive access.' };
    }

    // List files from Google Drive
    const response = await fetch(
      'https://www.googleapis.com/drive/v3/files?orderBy=createdTime%20desc&fields=files(id,name,mimeType,webViewLink,createdTime,size)&pageSize=50',
      {
        headers: {
          Authorization: `Bearer ${providerToken}`,
        },
      }
    );

    if (!response.ok) {
      const errText = await response.text();
      console.error('Google Drive list failure:', errText);
      return { success: false, error: `Google Drive list failed: ${response.status} ${response.statusText}` };
    }

    const result = await response.json();
    return { success: true, files: result.files || [] };
  } catch (err: any) {
    console.error('listGoogleDriveFiles error:', err);
    return { success: false, error: err?.message || String(err) };
  }
}

